import { Component } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LazyLoadEvent } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { PaginationFilter } from 'src/app/models/pagination-filter';
import { DetachedServicesReportData } from './detached-report-data';
import { DetachedServicesReportFilter } from './detached-report-filter';
import { DetachedServicesReportService } from 'src/app/services/detached-services-report.service';
import { Currency } from 'src/app/models/enums/currency';
import { CurrencyService } from 'src/app/services/currency.service';
import { ProductType } from 'src/app/models/enums';
import { MessageUtil } from 'src/app/utils/message.util';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-detached-services',
  templateUrl: './detached-services.component.html',
  styleUrls: ['./detached-services.component.scss']
})
export class DetachedServicesComponent {

  currentCurrency: Currency = Currency.BRL;
  filterForm: FormGroup;
  reportData: DetachedServicesReportData[] = [];
  loading = false;
  exportLoading = false;
  totalElements = 0;
  pageSize = 10;
  currentPage = 0;
  companyOptions: { label: string, value: number }[] = [
    { label: 'Todas', value: null },
    { label: 'Pousada', value: 0 },
    { label: 'Comércio', value: 25 },
  ];
  productTypeOptions = [];
  private destroy$ = new Subject<void>();

  constructor(
    private messageService: MessageUtil,
    private fb: FormBuilder,
    private detachedServicesReport: DetachedServicesReportService,
    private currencyService: CurrencyService
  ) {
    this.filterForm = this.fb.group({
      productId: [''],
      startDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth() - 1,
        1
      ),
      endDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        0
      ),
      companyId: [''],
      productType: [''],
    });
  }

  ngOnInit(): void {
    this.currentCurrency = this.currencyService.getCurrentCurrency();

    this.loadProductTypeOptions();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProductTypeOptions(): void {
    this.productTypeOptions = [
          { label: this.messageService.translateKey('PRODUCT'), value: ProductType.Product },
    { label: this.messageService.translateKey('SERVICE'), value: ProductType.Service },
    { label: this.messageService.translateKey('PERIODICAL-SERVICE'), value: ProductType.PeriodicalService },
    { label: this.messageService.translateKey('PERIODICAL-SERVICE-CUSTOMER'), value: ProductType.PeriodicalServiceCustomer },
    { label: this.messageService.translateKey('PACKAGE-SERVICE'), value: ProductType.PackageService }
    ];
    console.log(this.productTypeOptions);
  }

  loadData(): void {
    this.loading = true;
    const formValues = this.filterForm.value;
    const filters: DetachedServicesReportFilter = {
      companyId: formValues.companyId || null,
      productType: formValues.productType || null,
      startDate: formValues.startDate
        ? formValues.startDate.toISOString()
        : new Date(
            new Date().getFullYear(),
            new Date().getMonth() - 1,
            1
          ).toISOString(),
      endDate: formValues.endDate
        ? formValues.endDate.toISOString()
        : new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            0
          ).toISOString(),
    };

    const paginationFilter: PaginationFilter = {
      page: this.currentPage,
      size: this.pageSize,
      sort: 'id',
      order: 'ASC'
    };

    this.detachedServicesReport.getDetachedServicesReport(filters, paginationFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.reportData = response.content;
          console.log(this.reportData);
          this.totalElements = response.totalElements;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar dados do relatório:', error);
          this.reportData = [];
          this.totalElements = 0;
          this.loading = false;
        }
      });
  }

  changeCategory(): void {
    console.log(this.filterForm.value);
  }

  searchData(): void {
    this.currentPage = 0;
    this.loadData();
  }

  onLazyLoad(event: LazyLoadEvent): void {
    this.currentPage = Math.floor((event.first || 0) / (event.rows || 10));
    this.pageSize = event.rows || 10;
    this.loadData();
  }

  clearFilters(): void {
    this.filterForm.reset({
      customerName: '',
      serviceName: '',
      serviceStartDate: null,
      serviceEndDate: null,
      recurringServiceType: ''
    });
    this.currentPage = 0;
    this.loadData();
  }

  exportSimple(): void {
    if (this.exportLoading) return;
    this.exportLoading = true;
    const paginationFilter = this.createPaginationFilter();

    this.detachedServicesReport
      .getDetachedServicesReportSimple(
        this.filterForm.value,
        paginationFilter,
        'PDF'
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.handlePdfResponse(response, 'relatorio_simples.pdf');
          this.exportLoading = false;
        },
        error: (error) => {
          this.handleExportError(error);
          this.exportLoading = false;
        },
      });
  }

  exportByCustomer(): void {
    if (this.exportLoading) return;
    this.exportLoading = true;
    const paginationFilter = this.createPaginationFilter();

    this.detachedServicesReport
      .getDetachedServicesReportByCustomer(
        this.filterForm.value,
        paginationFilter,
        'PDF'
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.handlePdfResponse(response, 'relatorio_por_cliente.pdf');
          this.exportLoading = false;
        },
        error: (error) => {
          this.handleExportError(error);
          this.exportLoading = false;
        },
      });
  }

  private createPaginationFilter(): PaginationFilter {
    return {
      page: 0,
      size: 10000,
      sort: 'id',
      order: 'ASC',
    };
  }

  private handlePdfResponse(response: any, filename: string): void {
    if (response instanceof ArrayBuffer && response.byteLength > 0) {
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } else {
      console.log(response);
    }
  }

  private handleExportError(error: any): void {
    let errorMessage = 'Erro desconhecido';

    if (error.error instanceof ArrayBuffer) {
      const decoder = new TextDecoder('utf-8');
      try {
        const errorText = decoder.decode(error.error);
        const errorObj = JSON.parse(errorText);
        errorMessage = errorObj.message || errorObj.error || 'Erro no servidor';
      } catch {
        errorMessage = `Erro ${error.status}: ${error.statusText}`;
      }
    } else if (error.error && typeof error.error === 'object') {
      errorMessage =
        error.error.message ||
        error.error.error ||
        error.message ||
        'Erro no servidor';
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.status) {
      errorMessage = `Erro ${error.status}: ${error.statusText}`;
    }

    Swal.fire({
      icon: 'error',
      title: 'Erro',
      text: errorMessage,
    });
  }
}
